import { create } from 'zustand';

interface TelegramUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
}

interface AuthState {
  userId: number | null;
  username: string | null;
  telegramUser: TelegramUser | null;
  isDebug: boolean;
  initFromTelegram: () => boolean;
  setDebugUser: (id: number, name: string) => void;
  clear: () => void;
}

function readTelegramUser(): TelegramUser | null {
  if (typeof window === 'undefined') return null;
  const tg = window.Telegram?.WebApp;
  const user = tg?.initDataUnsafe?.user;
  if (!user || !user.id) return null;
  return user as TelegramUser;
}

export const useAuthStore = create<AuthState>((set) => ({
  userId: null,
  username: null,
  telegramUser: null,
  isDebug: false,
  initFromTelegram: () => {
    const user = readTelegramUser();
    if (!user) return false;
    set({
      userId: user.id,
      username: user.username || user.first_name || `user_${user.id}`,
      telegramUser: user,
      isDebug: false,
    });
    return true;
  },
  setDebugUser: (id, name) =>
    set({
      userId: id,
      username: name,
      telegramUser: {
        id,
        first_name: name,
        username: name,
      },
      isDebug: true,
    }),
  clear: () => set({ userId: null, username: null, telegramUser: null, isDebug: false }),
}));

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData?: string;
        initDataUnsafe?: {
          user?: TelegramUser;
          [k: string]: unknown;
        };
        expand?: () => void;
        ready?: () => void;
        close?: () => void;
        BackButton?: {
          show: () => void;
          hide: () => void;
          onClick: (cb: () => void) => void;
          offClick: (cb: () => void) => void;
        };
        MainButton?: {
          text: string;
          show: () => void;
          hide: () => void;
          onClick: (cb: () => void) => void;
          offClick: (cb: () => void) => void;
        };
        HapticFeedback?: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
          notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
          selectionChanged: () => void;
        };
        themeParams?: Record<string, string>;
        colorScheme?: 'light' | 'dark';
        viewportHeight?: number;
        viewportStableHeight?: number;
        platform?: string;
        version?: string;
        isVerticalSwipesEnabled?: boolean;
        disableVerticalSwipes?: () => void;
        enableVerticalSwipes?: () => void;
        requestFullscreen?: () => void;
        exitFullscreen?: () => void;
      };
    };
  }
}
